/**
 * Nav sidebar layout constants.
 *
 * These must stay in sync with the Tailwind classes in:
 *   - Drawer.tsx     (lg:w-20 / lg:w-56  — physical sidebar element width)
 *   - AppShell.tsx   (lg:pl-20 / lg:pl-56 — main content left padding)
 *
 * The main content left padding matches the sidebar width exactly so the content
 * sits flush against the sidebar edge (no asymmetric gap on the left).
 */

/** Physical width of the nav sidebar element (Tailwind lg:w-20 / lg:w-56). */
export const NAV_SIDEBAR_WIDTH_COLLAPSED = 80;
export const NAV_SIDEBAR_WIDTH_EXPANDED = 224;

/** Left padding applied to the main content area (Tailwind lg:pl-20 / lg:pl-56). */
export const CONTENT_LEFT_PADDING_COLLAPSED = 80;
export const CONTENT_LEFT_PADDING_EXPANDED = 224;

/**
 * How much left padding changes when the sidebar collapses or expands.
 * Used to adjust the side panel width so the visible content area stays constant.
 */
export const NAV_SIDEBAR_WIDTH_DIFF =
  CONTENT_LEFT_PADDING_EXPANDED - CONTENT_LEFT_PADDING_COLLAPSED; // 144

/** Extra space between the page and the side panel. The drag handle sits on the panel edge. */
export const PANEL_RIGHT_GAP = 0;

/**
 * Minimum width of the main content area while the side panel is in resize mode.
 * When the panel would push content narrower than this, it switches to overlay mode.
 */
export const MIN_CONTENT_WIDTH = 700;

/** Docked Case Partner chat-history column (Tailwind w-64). */
export const CASE_PARTNER_HISTORY_WIDTH = 256;

/** Left padding of the main content area for the current nav state. */
export const getContentLeftPadding = (navIsCollapsed: boolean): number =>
  navIsCollapsed ? CONTENT_LEFT_PADDING_COLLAPSED : CONTENT_LEFT_PADDING_EXPANDED;

/** Side panel width where content stops shrinking and the panel starts overlaying it. */
export const getSidePanelOverlayThreshold = (
  navIsCollapsed: boolean,
  reservedContentWidth = 0,
): number =>
  window.innerWidth
  - getContentLeftPadding(navIsCollapsed)
  - PANEL_RIGHT_GAP
  - MIN_CONTENT_WIDTH
  - reservedContentWidth;

/** Right inset for fixed content that should track the side panel until overlay mode. */
export const getSidePanelAwareRightInset = (
  panelWidth: number,
  navIsCollapsed: boolean,
  reservedContentWidth = 0,
): number =>
  Math.min(panelWidth, getSidePanelOverlayThreshold(navIsCollapsed, reservedContentWidth));

/**
 * Whether opening a side panel of this width would squeeze the main content
 * below MIN_CONTENT_WIDTH while the nav is still expanded. Used to auto-collapse
 * the left nav on smaller screens.
 */
export const shouldCollapseNavForSidePanel = (
  viewportWidth: number,
  panelWidth: number,
  reservedContentWidth = 0,
): boolean =>
  panelWidth
  > viewportWidth
    - CONTENT_LEFT_PADDING_EXPANDED
    - PANEL_RIGHT_GAP
    - MIN_CONTENT_WIDTH
    - reservedContentWidth;
